
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
  IconButton,
  alpha,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Inventory2Outlined,
  LocationOnOutlined,
  WarningAmberOutlined,
  NotesOutlined,
} from "@mui/icons-material";
import { useCargarSeriesProductos } from "@/store/erp/productos/useCargarSeriesProductos.store";
import { UseFormReturn } from "react-hook-form";
import { IngresoInventarioForm } from "@/shared/interfaces";
import { CustomAutocompleteArrString, CustomTextArea } from "@/shared/components/ui";
import { gridSizeMdLg12 } from "@/shared";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { useFetchMotivos } from "@/shared/actions/erp";
// Tema claro personalizado
const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#4A90A4",
      light: "#7BB5C7",
      dark: "#2E6B7D",
    },
    secondary: {
      main: "#7C9A92",
      light: "#A8C5BC",
      dark: "#5A7A72",
    },
    warning: {
      main: "#E8B86D",
      light: "#F5D9A8",
      dark: "#C99A4A",
    },
    background: {
      default: "#F8FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#2C3E50",
      secondary: "#607D8B",
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});



interface SeriesReentryModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  form: UseFormReturn<IngresoInventarioForm, any, undefined>;
  series?: string[];
}

export default function ModalReingresoSeries({
  open,
  onClose,
  onConfirm,
  form
}: SeriesReentryModalProps) {


  //fetch 

  const { data: motivos } = useFetchMotivos({
    modulo: 'REINGRESO_SERIES'
  });

  // hook and store
  const series = useCargarSeriesProductos(s => s.dataReingreso)?.series_reingreso || [];


  // handle
  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header con gradiente suave */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha("#4A90A4", 0.1)} 0%, ${alpha("#7C9A92", 0.1)} 100%)`,
            borderBottom: `1px solid ${alpha("#4A90A4", 0.15)}`,
            position: "relative",
            p: 3,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: alpha("#4A90A4", 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                backgroundColor: alpha("#E8B86D", 0.2),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberOutlined
                sx={{ fontSize: 28, color: "warning.dark" }}
              />
            </Box>
            <Box>
              <DialogTitle
                sx={{
                  p: 0,
                  fontSize: "1.35rem",
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Reingreso de Series Detectado
              </DialogTitle>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Se han identificado series que ya existían previamente en el sistema
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Lista de Series */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Inventory2Outlined sx={{ fontSize: 18, color: "primary.main" }} />
            Series a reingresar ({series.length})
          </Typography>

          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#F8FAFB", 0.8),
              border: `1px solid ${alpha("#4A90A4", 0.12)}`,
              borderRadius: 2,
              p: 2,
              mb: 3,
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {series.map((serie) => (
                <Chip
                  key={serie.serie}
                  label={
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", lineHeight: 1.2 }}
                      >
                        {serie.serie}
                      </Typography>

                    </Box>
                  }
                  sx={{
                    height: "auto",
                    py: 0.75,
                    px: 0.5,
                    backgroundColor: alpha("#4A90A4", 0.08),
                    border: `1px solid ${alpha("#4A90A4", 0.2)}`,
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>

          {/* Selectores de Bodega y Ubicación */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LocationOnOutlined sx={{ fontSize: 18, color: "secondary.main" }} />
            Destino del reingreso
          </Typography>

          <Box sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 4,
            mb: 3
          }}>

            {/* Bloque Bodega */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontWeight: "bold" }}>Bodega:</Typography>
              <Typography>{form.getValues("bodega_nombre")}</Typography>
            </Box>

            {/* Bloque Ubicación */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontWeight: "bold" }}>Ubicación:</Typography>
              <Typography>{form.getValues("ubicacion_nombre")}</Typography>
            </Box>

          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FormatListBulletedIcon sx={{ fontSize: 18, color: "primary.main" }} />
            Motivo de Solicitud
          </Typography>


          <CustomAutocompleteArrString
            label="Seleccionar"
            required={true}
            name="motivo_emisor"
            options={motivos?.data.data.map(m => m.nombre) || []}
            control={form.control}
            defaultValue={form.getValues().motivo_emisor || ''}
            error={undefined}
            helperText={undefined}
            isLoadingData={false}
            size={gridSizeMdLg12}
          />

          <Divider sx={{ my: 2 }} />

          {/* Campo de Observaciones */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <NotesOutlined sx={{ fontSize: 18, color: "primary.main" }} />
            Observacion Adicional
          </Typography>

          <CustomTextArea
            type="none"
            typeOfEntry='observacion'
            name="observacion_emisor"
            control={form.control}
            label=""
            required={true}
            maxRows={4}
            minRows={4}
            error={undefined}
            helperText={undefined}
          />

          <Divider sx={{ my: 2 }} />

        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              flex: 1,
              py: 1.25,
              borderRadius: 1,
              borderColor: alpha("#607D8B", 0.3),
              color: "text.secondary",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: alpha("#607D8B", 0.05),
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            //disabled={!bodega || !ubicacion}
            sx={{
              flex: 1,
              py: 1.25,
              borderRadius: 1,
              fontWeight: 500,
              textTransform: "none",
              backgroundColor: "primary.main",
              boxShadow: `0 4px 12px ${alpha("#4A90A4", 0.3)}`,
              "&:hover": {
                backgroundColor: "primary.dark",
                boxShadow: `0 6px 16px ${alpha("#4A90A4", 0.4)}`,
              },
              "&:disabled": {
                backgroundColor: alpha("#4A90A4", 0.3),
                color: "#fff",
              },
            }}
          >
            Generar una solicitud de reingreso
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
